import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./NavigationBar.css";
import logo from "./logo.png";
import logoDarkPng from "./dark-logo.png";

const NavigationBar = ({ handleContactClick, theme, toggleTheme, menuOpen, setMenuOpen }) => {
  const navigate = useNavigate();
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);

  const activityReports = [
    { label: 'February 2018', url: 'https://drive.google.com/file/d/12nlRUAjLpD6q_PegaiwZkM2rAwkaFHyi/view' },
    { label: 'March & April 2018', url: 'https://drive.google.com/file/d/1k95b2NXXahvYy1vIt3t-cDfJewC3DWKV/view?usp=sharing' },
    { label: 'May & June 2018', url: 'https://drive.google.com/file/d/1O8egmLiPW49acZtJr9xx-oNE8a4l84wJ/view?usp=sharing' },
  ];

  // Scroll-spy: track which section is in view and set active nav link
  const [activeSection, setActiveSection] = useState("home");

  const menuRef = React.useRef(null);
  const toggleRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If menu is open and the click is NOT inside the dropdown menu AND NOT on the toggle button
      if (
        menuOpen && 
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [menuOpen, setMenuOpen]);

  useEffect(() => {
    const ids = ["home", "about", "what-we-do", "media", "projects"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        // trigger when the section is near the middle of the viewport
        root: null,
        rootMargin: "-40% 0px -40% 0px",
        threshold: 0,
      }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavClick = (e, hash) => {
    // Only handle internal hashes
    if (!hash || !hash.startsWith('#')) return;
    e.preventDefault();
    setMenuOpen(false);
    const id = hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // update the URL hash without jumping
      window.history.pushState(null, '', `/${hash}`);
      return;
    }

    // If the element isn't on the current page (for example when on /calendar),
    // navigate to the root with the hash. The App will handle the scroll.
    navigate(`/#${id}`);
    setMenuOpen(false);
  };

  const handleLogoClick = (e) => {
    if (window.location.pathname === '/') {
      // If already on homepage, force refresh
      e.preventDefault();
      window.location.reload();
    }
    // Always close menu on logo click
    setMenuOpen(false);
  };

  return (
    <>
      <header className="App-header">
  <nav className="nav-bar">
  <div className="nav-inner">
        <div className="logo">
          <Link to="/" className="logo-link" aria-label="Go to homepage" onClick={handleLogoClick}>
            <div className="logo-container">
              <img src={logo} alt="Being Volunteer USA" className="logo-img logo-light" width="140" height="44" aria-hidden="true" />
              <img src={logoDarkPng} alt="Being Volunteer USA" className="logo-img logo-dark" width="140" height="44" aria-hidden="true" />
              <span className="logo-text">BeingVolunteerUSA</span>
            </div>
          </Link>
        </div>

        <ul className={`nav-links ${menuOpen ? "active" : ""}`} ref={menuRef}>
          <li>
            <a href="/#about" onClick={(e) => handleNavClick(e, '#about')} className={activeSection === "about" ? "active" : ""}>
              About Us
            </a>
          </li>
          <li>
            <a href="/#what-we-do" onClick={(e) => handleNavClick(e, '#what-we-do')} className={activeSection === "what-we-do" ? "active" : ""}>
              What We Do
            </a>
          </li>
          <li>
            <a href="/#media" onClick={(e) => handleNavClick(e, '#media')} className={activeSection === "media" ? "active" : ""}>
              Media
            </a>
          </li>
          <li 
            className="dropdown-container"
            onMouseEnter={() => !menuOpen && setReportsDropdownOpen(true)}
            onMouseLeave={() => !menuOpen && setReportsDropdownOpen(false)}
          >
            <a 
              href="#reports" 
              className="dropdown-trigger"
              onClick={(e) => {
                if (menuOpen || window.innerWidth <= 768) {
                  e.preventDefault();
                  setReportsDropdownOpen(!reportsDropdownOpen);
                }
              }}
            >
              Activity Reports
            </a>
            <ul 
              className={`dropdown-menu ${reportsDropdownOpen ? "active" : ""}`}
              onMouseEnter={() => !menuOpen && setReportsDropdownOpen(true)}
              onMouseLeave={() => !menuOpen && setReportsDropdownOpen(false)}
              onClick={(e) => e.stopPropagation()}
            >
              {activityReports.map((report, index) => (
                <li key={index}>
                  <a 
                    href={report.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setReportsDropdownOpen(false);
                      if (menuOpen) setMenuOpen(false);
                    }}
                  >
                    {report.label}
                  </a>
                </li>
              ))}
            </ul>
          </li>
          <li>
            <a href="/#projects" onClick={(e) => handleNavClick(e, '#projects')} className={activeSection === "projects" ? "active" : ""}>
              Our Projects
            </a>
          </li>
          <li>
            <button onClick={handleContactClick} className="header-contact-button">
              Contact
            </button>
          </li>
          <li className="theme-toggle-li-mobile">
            <div className="theme-toggle theme-toggle-mobile">
              <button className="theme-toggle-btn" onClick={toggleTheme} aria-label="Toggle dark mode">
                {theme === 'dark' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
                )}
              </button>
            </div>
          </li>

        </ul>

        <div className="right-controls">
          <div className="theme-toggle theme-toggle-desktop">
            <button className="theme-toggle-btn" onClick={toggleTheme} aria-label="Toggle dark mode">
              {theme === 'dark' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
              )}
            </button>
          </div>

          <button
            className="menu-toggle"
            ref={toggleRef}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            title={menuOpen ? 'Close menu' : 'Open menu'}
          >
            ☰
          </button>
        </div>
  </div>
  </nav>
      </header>
      <div className="nav-spacer" aria-hidden="true" />
    </>
  );
};

export default NavigationBar;
